import { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../firebase/config";
import userService from "../services/userService";
import invitationService from "../services/invitationService";
import otpAuthService from "../services/otpAuthService";

// User roles
export const USER_ROLES = {
  ADMIN: "admin",
  MENTOR: "mentor",
  MENTOR_PENDING: "mentor_pending",
  STUDENT: "student",
};

// Mentor message character limit
export const MENTOR_MESSAGE_CHARACTER_LIMIT = 140;

// Create the context
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to sign up a new user with OTP
  async function signup(email, userData, role = USER_ROLES.STUDENT) {
    try {
      // For mentor registration with invitation, skip certain existence checks
      const isInvitedMentor =
        role === USER_ROLES.MENTOR && userData.invitationId;

      // Perform comprehensive user existence check first (includes all sources)
      const userExists = await checkUserExists(email, isInvitedMentor);
      if (userExists) {
        throw new Error("auth/email-already-in-use");
      }

      // Store user data temporarily in sessionStorage
      const tempUserData = {
        email,
        userData,
        role,
        timestamp: Date.now(),
      };
      sessionStorage.setItem("pendingSignup", JSON.stringify(tempUserData));

      // Send OTP for registration
      const result = await otpAuthService.sendOTP(email, "signup");

      return { email, otpSent: true, expiresIn: result.expiresIn };
    } catch (error) {
      console.error("Signup error:", error);
      sessionStorage.removeItem("pendingSignup");
      throw error;
    }
  }

  // Function to check if user exists in the system
  async function checkUserExists(email) {
    try {
      // Primary check: Use Firebase Auth to check if user exists
      const { fetchSignInMethodsForEmail } = await import("firebase/auth");

      try {
        const signInMethods = await fetchSignInMethodsForEmail(auth, email);

        // If Firebase Auth has sign-in methods for this email, user exists
        if (signInMethods.length > 0) {
          return true;
        }
      } catch (authError) {
        // Some regions/configs may not allow this check, continue with other methods
      }

      // Secondary check: Look for pending signup data in sessionStorage for same email
      const pendingSignupStr = sessionStorage.getItem("pendingSignup");
      if (pendingSignupStr) {
        try {
          const pendingSignup = JSON.parse(pendingSignupStr);
          if (pendingSignup.email === email) {
            return true;
          }
        } catch (parseError) {
          // Continue if sessionStorage parsing fails
        }
      }

      // Use the new userService for comprehensive user existence check
      return await userService.checkUserExists(email);
    } catch (error) {
      console.error("Error checking user existence:", error);
      // On error, be conservative and allow registration to prevent false positives
      // Better to have potential duplicate than block legitimate users
      return false;
    }
  }

  // OTP login - replaces email link
  async function sendLoginOTP(email) {
    try {
      // Check if user exists before sending OTP
      const userExists = await checkUserExists(email);
      if (!userExists) {
        throw new Error("auth/user-not-found");
      }

      return await otpAuthService.sendOTP(email, "login");
    } catch (error) {
      console.error("Error sending OTP:", error);
      throw error;
    }
  }

  // Function to verify OTP for signup and complete registration
  async function verifySignupOTP(email, otp) {
    try {
      // Get pending signup data
      const pendingSignupStr = sessionStorage.getItem("pendingSignup");
      if (!pendingSignupStr) {
        throw new Error("No pending signup found");
      }

      const pendingSignup = JSON.parse(pendingSignupStr);
      if (pendingSignup.email !== email) {
        throw new Error("Email mismatch");
      }

      // Verify OTP and get auth result
      const result = await otpAuthService.verifyOTP(email, otp, "signup");

      if (result.success && result.user) {
        // Handle profile image upload for mentors if exists
        let processedUserData = { ...pendingSignup.userData };
        if (
          pendingSignup.role === USER_ROLES.MENTOR &&
          processedUserData.profileImageFile
        ) {
          try {
            const { ref, uploadBytes, getDownloadURL } = await import(
              "firebase/storage"
            );
            const { storage } = await import("../firebase/config");

            // Convert base64 data URL to Blob
            const base64Data = processedUserData.profileImageFile;
            const response = await fetch(base64Data);
            const blob = await response.blob();

            const fileName = `mentor-icons/${result.user.uid}.jpg`;
            const storageRef = ref(storage, fileName);
            await uploadBytes(storageRef, blob);
            processedUserData.アイコンURL = await getDownloadURL(storageRef);
          } catch (error) {
            console.error("Error uploading profile image:", error);
          }
          delete processedUserData.profileImageFile;
        }

        // Use userService to create user with related data
        const additionalData = {};
        if (pendingSignup.role === USER_ROLES.STUDENT) {
          additionalData.studentData = processedUserData;
          console.log('Preparing student data for save:', additionalData.studentData);
        } else if (pendingSignup.role === USER_ROLES.MENTOR) {
          additionalData.mentorData = processedUserData;
        }

        await userService.createUserWithRelatedData(
          result.user.uid,
          email,
          pendingSignup.role,
          additionalData
        );

        // Update invitation if ID exists
        if (
          pendingSignup.role === USER_ROLES.MENTOR &&
          processedUserData.invitationId
        ) {
          await invitationService.updateInvitationStatus(
            processedUserData.invitationId,
            "completed",
            { userId: result.user.uid }
          );
        }

        // Clear pending signup
        sessionStorage.removeItem("pendingSignup");

        // Force role refresh
        await fetchUserRole(result.user.uid);
        await new Promise((resolve) => setTimeout(resolve, 500));

        return result;
      }

      throw new Error("OTP verification failed");
    } catch (error) {
      console.error("Signup OTP verification error:", error);
      throw error;
    }
  }

  // Legacy login function (to be removed)
  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // OTP Authentication methods - these replace the duplicate methods
  async function verifyLoginOTP(email, otp) {
    try {
      const result = await otpAuthService.verifyOTP(email, otp, "login");
      if (result.success) {
        // Force role refresh to ensure UI is updated
        if (result.user) {
          await fetchUserRole(result.user.uid);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
      return result;
    } catch (error) {
      console.error("Error verifying OTP:", error);
      throw error;
    }
  }

  // Function to log out a user
  function logout() {
    return signOut(auth);
  }

  // Function to send a password reset email
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Function to check user role
  async function fetchUserRole(uid) {
    try {
      const role = await userService.getUserRole(uid);

      setUserRole(role);

      return role;
    } catch (error) {
      // 権限エラーの場合は警告レベルに下げる
      if (error.code === 'permission-denied' || error.message.includes('insufficient permissions')) {
        console.warn("Permission denied while fetching user role - user may not be fully authenticated yet:", error.message);
        // デフォルトロールを設定して続行
        setUserRole(USER_ROLES.STUDENT);
        return USER_ROLES.STUDENT;
      }
      
      console.error("Error fetching user role:", error);
      setUserRole(USER_ROLES.STUDENT);
      return USER_ROLES.STUDENT;
    }
  }

  // Function to refresh user role (can be called when role might have changed)
  async function refreshUserRole() {
    if (currentUser && currentUser.uid) {
      try {
        await fetchUserRole(currentUser.uid);
      } catch (error) {
        // 権限エラーの場合は無視（認証状態の問題である可能性が高い）
        if (error.code === 'permission-denied' || error.message.includes('insufficient permissions')) {
          console.warn('Permission denied while refreshing user role - user may not be fully authenticated yet');
          return;
        }
        throw error;
      }
    }
  }

  // Function to check if user has required access level
  function checkAccess(requiredRole) {
    const roleHierarchy = {
      [USER_ROLES.ADMIN]: 3,
      [USER_ROLES.MENTOR]: 2,
      [USER_ROLES.MENTOR_PENDING]: 1.5,
      [USER_ROLES.STUDENT]: 1,
    };
    return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
  }

  // Session timeout functionality removed - users remain logged in

  // Function to check email verification status
  function checkEmailVerified() {
    // OTP authentication always verifies email access
    if (currentUser) {
      return true;
    }
    return false;
  }

  // Set up auth state observer and clean up on unmount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          await fetchUserRole(user.uid);
          // Force a small delay to ensure UI updates properly
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error) {
          console.error("Error fetching user role:", error);
        }
      } else {
        // ユーザーがログアウトした場合はroleもリセット
        setUserRole(null);
      }
      setLoading(false);
    });

    // Cleanup function
    return () => {
      unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    userRole,
    checkAccess,
    signup,
    login,
    sendLoginOTP,
    verifyLoginOTP,
    verifySignupOTP,
    logout,
    resetPassword,
    checkEmailVerified,
    checkUserExists,
    loading,
    refreshUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
